import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap } from 'react-leaflet';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Search, MapPin, Clock, ExternalLink, Filter, Loader2, QrCode, Camera, Sparkles, Home, Package, Share2, Copy, ChevronDown, ChevronUp, Trophy, X } from 'lucide-react';
import api, { API_BASE } from '../services/api';
import { useMapFilter } from '../contexts/MapFilterContext';
import { HomepageLeaderboard } from './HomepageLeaderboard';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import '../mapStyles.css';

// Fix for default markers in react-leaflet
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons for different types of markers (smaller size)
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 1px 3px rgba(0,0,0,0.3);"></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
};

// Custom pin-style marker for current location (similar to classic red pin)
const createPinIcon = (color = '#dc2626') => {
  return L.divIcon({
    className: 'custom-pin-marker',
    html: `
      <div style="position: relative; width: 24px; height: 24px;">
        <div style="
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 20px;
          height: 20px;
          background: ${color};
          border: 2px solid white;
          border-radius: 50% 50% 50% 0;
          transform: translateX(-50%) rotate(-45deg);
          box-shadow: 0 2px 6px rgba(0,0,0,0.4);
        "></div>
        <div style="
          position: absolute;
          top: 3px;
          left: 50%;
          transform: translateX(-50%);
          width: 8px;
          height: 8px;
          background: white;
          border-radius: 50%;
          transform: translateX(-50%) rotate(-45deg);
        "></div>
      </div>
    `,
    iconSize: [24, 24],
    iconAnchor: [12, 20], // Anchor at the bottom point of the pin
  });
};

// Traveling item icons (existing)
const startIcon = createCustomIcon('#10b981'); // emerald
const currentIcon = createPinIcon('#dc2626'); // red pin for current location
const visitedIcon = createCustomIcon('#3b82f6'); // blue

// Stationary location icons (NEW - pin style)
const stationaryIcon = createPinIcon('#10b981'); // emerald/green pin for stationary locations
const stationaryActiveIcon = createPinIcon('#f59e0b'); // amber pin for active/selected stationary

export const InteractiveMap = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    itemTypeFilter, 
    setItemTypeFilter, 
    searchTerm, 
    setSearchTerm,
    userLocation: contextUserLocation,
    setUserLocation: setContextUserLocation,
    shouldZoomToUser,
    setShouldZoomToUser,
    privateTagMode,
    initialPrivateTag,
    theme
  } = useMapFilter();
  
  // Theme-aware color classes
  const themeColors = {
    gradientClass: theme?.gradientFrom && theme?.gradientTo 
      ? `bg-gradient-to-r from-${theme.gradientFrom} to-${theme.gradientTo}`
      : 'bg-gradient-to-r from-emerald-800 to-emerald-700',
    primaryText: theme?.primary ? `text-${theme.primary}-600` : 'text-emerald-700',
    secondaryText: theme?.secondary ? `text-${theme.secondary}-600` : 'text-amber-700',
    accentBg: theme?.accent ? `bg-${theme.accent}-100` : 'bg-emerald-50',
    borderFocus: theme?.primary ? `focus:border-${theme.primary}-500` : 'focus:border-emerald-600',
    lightBg: theme?.primary ? `text-${theme.primary}-100` : 'text-emerald-100',
    spinnerColor: theme?.primary ? `text-${theme.primary}-600` : 'text-emerald-700'
  };
  
  const handleEngagementItemClick = (uid) => {
    if (uid) {
      navigate(`/item/${uid}`);
    }
  };
  
  // Handle location guestbook click - zoom to user location
  const handleLocationGuestbookClick = () => {
    setItemTypeFilter('stationary');
    
    // Request user's location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(userPos);
          setContextUserLocation(userPos);
          setShouldZoomToUser(true);
          
          // Zoom map to user location (zoom level 9 = ~100-120 mile radius)
          // This is 2 levels out from previous level 11
          if (mapRef) {
            mapRef.setView([userPos.lat, userPos.lng], 9);
            
            // Trigger "Search This Area" after zoom
            setTimeout(() => {
              setUserTriggeredSearch(true);
            }, 500);
          }
        },
        (error) => {
          console.warn('Location access denied or unavailable:', error);
          // Still switch to stationary, just don't zoom
          setItemTypeFilter('stationary');
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 60000 // Cache for 1 minute
        }
      );
    } else {
      // Geolocation not supported, just switch filter
      setItemTypeFilter('stationary');
    }
  };
  
  const [selectedItem, setSelectedItem] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAllItems, setShowAllItems] = useState(false);
  const [engagementStats, setEngagementStats] = useState(null);
  const [basicStats, setBasicStats] = useState(null);

  // New search enhancement states
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResultsExpanded, setSearchResultsExpanded] = useState(false);
  const [maxVisibleResults, setMaxVisibleResults] = useState(12);
  const [sortBy, setSortBy] = useState('relevance'); // relevance, recent, distance, stories
  const [statsLoading, setStatsLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [statsExpanded, setStatsExpanded] = useState(false); // Mobile stats collapsed by default
  const [engagementExpanded, setEngagementExpanded] = useState(false); // Mobile engagement collapsed by default
  const [isMobile, setIsMobile] = useState(false);
  const [showSearchButton, setShowSearchButton] = useState(false);
  const [loadingMoreItems, setLoadingMoreItems] = useState(false);
  const [userTriggeredSearch, setUserTriggeredSearch] = useState(false);
  const [mapInteractionEnabled, setMapInteractionEnabled] = useState(false); // Track if user has activated map
  const [autoToggleEnabled, setAutoToggleEnabled] = useState(false); // Disabled by default - show traveling items only
  const [autoToggleCount, setAutoToggleCount] = useState(0); // Track toggle count for display
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    hasStories: false,
    hasTravel: false,
    minDistance: 0,
    recentActivity: false // Last 30 days
  });
  
  // User filter for "My Items" view
  const [showMyItemsOnly, setShowMyItemsOnly] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  
  // Selected marker for detailed view in sidebar
  const [selectedMarkerItem, setSelectedMarkerItem] = useState(null);
  
  // Local search input state - only commits to searchTerm on Enter/button press
  const [searchInput, setSearchInput] = useState(searchTerm || '');
  
  // Sync searchInput when searchTerm changes externally (e.g. URL params, initial load)
  useEffect(() => {
    setSearchInput(searchTerm || '');
  }, [searchTerm]);
  
  const commitSearch = () => {
    setSearchTerm(searchInput);
  };
  
  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };
  
  // Pending geo-zoom for guestbook page (set by fetchItems, consumed by effect)
  const [pendingGeoZoom, setPendingGeoZoom] = useState(null);

  // Auto-zoom to guestbook location when map and geolocation are both ready
  useEffect(() => {
    if (pendingGeoZoom && mapRef) {
      const { lat, lng } = pendingGeoZoom;
      mapRef.setView([lat, lng], 10, { animate: true });
      setPendingGeoZoom(null);
      // Auto-trigger search after zoom settles
      setTimeout(() => {
        setShowSearchButton(true);
      }, 800);
    }
  }, [pendingGeoZoom, mapRef]);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-toggle between traveling items and guestbook locations every 50 seconds
  // Stops when user manually selects a filter
  useEffect(() => {
    if (!autoToggleEnabled) return;
    
    const toggleInterval = setInterval(() => {
      setItemTypeFilter(current => {
        // Toggle between 'traveling' and 'stationary'
        const newFilter = current === 'traveling' ? 'stationary' : 'traveling';
        setAutoToggleCount(prev => prev + 1);
        return newFilter;
      });
    }, 50000); // 50 seconds
    
    return () => clearInterval(toggleInterval);
  }, [autoToggleEnabled, setItemTypeFilter]);

  // Function to handle manual filter selection (stops auto-toggle)
  const handleManualFilterSelect = (filterType) => {
    setAutoToggleEnabled(false); // Stop auto-toggling
    setItemTypeFilter(filterType);
  };
  
  // Get current user's email from localStorage
  useEffect(() => {
    const userEmail = localStorage.getItem('userEmail');
    if (userEmail) {
      setCurrentUserEmail(userEmail);
    }
  }, []);

  // Map reference component
  const MapEvents = () => {
    const map = useMap();
    
    useEffect(() => {
      setMapRef(map);
      
      // Add double-click event handler for zoom and recenter
      const handleDoubleClick = (e) => {
        const clickedLatLng = e.latlng;
        const currentZoom = map.getZoom();
        const newZoom = currentZoom + 1;
        
        // Zoom and recenter to the clicked location
        map.setView(clickedLatLng, newZoom, {
          animate: true,
          duration: 0.5
        });
      };
      
      // Show "Search This Area" button when map moves
      const handleMoveEnd = () => {
        setShowSearchButton(true);
      };
      
      // Attach the event listeners
      map.on('dblclick', handleDoubleClick);
      map.on('moveend', handleMoveEnd);
      
      // Cleanup function to remove event listeners
      return () => {
        map.off('dblclick', handleDoubleClick);
        map.off('moveend', handleMoveEnd);
      };
    }, [map]);
    
    return null;
  };
  
  // Function to search items in current map view
  const handleSearchThisArea = async () => {
    if (!mapRef) return;
    
    // Save current map view to restore after search
    const currentZoom = mapRef.getZoom();
    const currentCenter = mapRef.getCenter();
    
    setLoadingMoreItems(true);
    setShowSearchButton(false);
    setUserTriggeredSearch(true); // Flag to prevent auto-zoom
    
    try {
      const bounds = mapRef.getBounds();
      
      // Calculate approximate area size for user feedback
      const latDiff = bounds.getNorth() - bounds.getSouth();
      const lngDiff = bounds.getEast() - bounds.getWest();
      const areaSize = latDiff * lngDiff;
      
      // Show warning for very large areas
      if (areaSize > 100) { // Roughly half of USA
        const confirmSearch = window.confirm(
          'You are searching a very large area. This may take a while and return many results.\n\n' +
          'Consider zooming in to a smaller area for faster results.\n\n' +
          'Continue with this search?'
        );
        if (!confirmSearch) {
          setLoadingMoreItems(false);
          setShowSearchButton(true);
          setUserTriggeredSearch(false);
          return;
        }
      }
      
      console.log(`🔍 Searching map area: lat ${bounds.getSouth().toFixed(2)} to ${bounds.getNorth().toFixed(2)}, lng ${bounds.getWest().toFixed(2)} to ${bounds.getEast().toFixed(2)}`);
      
      const response = await fetch(
        `${API_BASE}/api/map-items?` +
        `user_lat=${currentCenter.lat}&user_lng=${currentCenter.lng}&` +
        `limit_traveling=${itemTypeFilter === 'stationary' ? 0 : 100}&limit_stationary=${itemTypeFilter === 'stationary' ? 500 : 0}&` +
        `bounds_north=${bounds.getNorth()}&bounds_south=${bounds.getSouth()}&` +
        `bounds_east=${bounds.getEast()}&bounds_west=${bounds.getWest()}`
      );
      const data = await response.json();
      
      // Merge new items with existing ones, avoiding duplicates
      const existingUids = new Set(items.map(item => item.uid));
      const newItems = data.items.filter(item => !existingUids.has(item.uid));
      
      setItems([...items, ...newItems]);
      
      // IMPORTANT: Restore the map view to prevent auto-zoom
      setTimeout(() => {
        if (mapRef) {
          mapRef.setView(currentCenter, currentZoom, { animate: false });
          console.log(`🗺️ Map view restored: zoom ${currentZoom}, center [${currentCenter.lat.toFixed(4)}, ${currentCenter.lng.toFixed(4)}]`);
        }
      }, 100);
      
      // Show user feedback
      if (newItems.length === 0) {
        alert('No new items found in this area. Try zooming out or moving to a different location.');
      } else if (newItems.length >= 200) {
        alert(`Found ${newItems.length} items! There may be more items in this area. Zoom in for more specific results.`);
      } else {
        console.log(`✅ Loaded ${newItems.length} new items in this area`);
      }
    } catch (error) {
      console.error('Error loading items for area:', error);
      alert('Error loading items. Please try again.');
    } finally {
      setLoadingMoreItems(false);
      // Reset flag after a delay to allow map to stabilize
      setTimeout(() => setUserTriggeredSearch(false), 1000);
    }
  };

  // Component to handle auto-zoom to user location with nearby items
  const AutoZoomToLocation = () => {
    const map = useMap();
    
    useEffect(() => {
      // Request user location on mount
      if (!userLocation && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setUserLocation([latitude, longitude]);
            
            // Find 35-50 nearest items to user location
            const itemsWithCoords = mapData.markers.map(marker => ({
              ...marker,
              distance: Math.sqrt(
                Math.pow(marker.position[0] - latitude, 2) + 
                Math.pow(marker.position[1] - longitude, 2)
              )
            })).sort((a, b) => a.distance - b.distance);
            
            // Get 35-50 nearest items
            const nearbyItems = itemsWithCoords.slice(0, 50);
            
            if (nearbyItems.length > 0) {
              // Create bounds from nearby items
              const coordinates = nearbyItems.map(item => item.position);
              coordinates.push([latitude, longitude]); // Include user location
              
              const bounds = L.latLngBounds(coordinates);
              map.fitBounds(bounds, { 
                padding: [50, 50],
                maxZoom: 9,  // Reduced from 10 to show more area
                animate: true 
              });
            } else {
              // Fallback to simple zoom if no items found
              map.setView([latitude, longitude], 7, { animate: true });  // Reduced from 8 to show more area
            }
          },
          (error) => {
            console.log('Location access denied or unavailable:', error.message);
            // Keep default USA view if location is denied
          }
        );
      }
    }, [map]);
    
    return null;
  };

  // Component to control all map interactions - disabled by default until user explicitly interacts
  const MapInteractionHandler = () => {
    const map = useMap();
    
    useEffect(() => {
      // Initially disable ALL map interactions to allow page scrolling
      map.scrollWheelZoom.disable();
      map.dragging.disable();
      map.touchZoom.disable();
      map.doubleClickZoom.disable();
      
      // Function to enable all map interactions
      const enableMapInteractions = () => {
        map.scrollWheelZoom.enable();
        map.dragging.enable();
        map.touchZoom.enable();
        map.doubleClickZoom.enable();
        setMapInteractionEnabled(true);
      };
      
      // Listen for zoom control clicks via zoomend event
      const handleZoom = () => {
        if (!map.dragging.enabled()) {
          enableMapInteractions();
        }
      };
      
      map.on('zoomend', handleZoom);
      
      return () => {
        map.off('zoomend', handleZoom);
      };
    }, [map]);
    
    return null;
  };

  // Auto-fit map to show recent scanned items (mobile optimized)
  useEffect(() => {
    if (isMobile && mapRef && items.length > 0 && !userLocation && !userTriggeredSearch) {
      // Get the last 25 items with recent scans
      const recentItems = [...items]
        .filter(item => {
          const stories = item.stories || [];
          return stories.some(story => 
            story.coordinates && 
            story.coordinates.lat && 
            story.coordinates.lng
          );
        })
        .sort((a, b) => {
          const aLatest = Math.max(...(a.stories || []).map(s => 
            new Date(s.timestamp || s.createdAt || s.date || 0).getTime()
          ));
          const bLatest = Math.max(...(b.stories || []).map(s => 
            new Date(s.timestamp || s.createdAt || s.date || 0).getTime()
          ));
          return bLatest - aLatest;
        })
        .slice(0, 25); // Last 25 scanned items
      
      // Collect all coordinates from recent items
      const coordinates = [];
      recentItems.forEach(item => {
        (item.stories || []).forEach(story => {
          if (story.coordinates && story.coordinates.lat && story.coordinates.lng) {
            coordinates.push([story.coordinates.lat, story.coordinates.lng]);
          }
        });
      });
      
      if (coordinates.length > 0) {
        try {
          // Fit bounds to show all recent items
          const bounds = L.latLngBounds(coordinates);
          mapRef.fitBounds(bounds, {
            padding: [50, 50], // Add padding around the bounds
            maxZoom: 9 // Reduced from 10 to show more area on mobile
          });
          console.log(`📱 Mobile map fitted to ${coordinates.length} recent coordinates from ${recentItems.length} items`);
        } catch (error) {
          console.error('Error fitting bounds:', error);
        }
      }
    }
  }, [isMobile, mapRef, items, userLocation, userTriggeredSearch]);

  // Get user location when home button is clicked and show nearby items
  const handleHomeClick = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
          
          if (mapRef) {
            // Calculate distances to all items
            const itemsWithDistance = mapDisplayItems.map(item => {
              const coords = item.coordinates;
              if (coords && coords.lat && coords.lng) {
                // Simple distance calculation (Haversine formula)
                const R = 3959; // Earth's radius in miles
                const dLat = (coords.lat - location.lat) * Math.PI / 180;
                const dLon = (coords.lng - location.lng) * Math.PI / 180;
                const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                         Math.cos(location.lat * Math.PI / 180) * Math.cos(coords.lat * Math.PI / 180) *
                         Math.sin(dLon/2) * Math.sin(dLon/2);
                const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
                const distance = R * c;
                
                return { ...item, distanceFromUser: distance };
              }
              return { ...item, distanceFromUser: Infinity };
            });
            
            // Sort by distance and get 10-20 nearest items
            const nearbyItems = itemsWithDistance
              .filter(item => item.distanceFromUser !== Infinity)
              .sort((a, b) => a.distanceFromUser - b.distanceFromUser)
              .slice(0, 20); // Get closest 20 items
            
            if (nearbyItems.length > 0) {
              // Fit map to show these nearby items
              const bounds = L.latLngBounds(
                nearbyItems.map(item => [item.coordinates.lat, item.coordinates.lng])
              );
              mapRef.fitBounds(bounds, { 
                padding: [50, 50],
                maxZoom: 11 // Reduced from 12 to show more area
              });
              
              console.log(`🏠 Showing ${nearbyItems.length} nearby items within ${Math.round(nearbyItems[nearbyItems.length-1].distanceFromUser)} miles`);
            } else {
              // No nearby items, just center on user location
              mapRef.setView([location.lat, location.lng], 8);
            }
          }
        },
        (error) => {
          console.log('Geolocation error:', error);
          alert('Unable to access your location. Please check your browser permissions.');
        }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  // Load search term from URL on component mount and handle URL changes
  useEffect(() => {
    const urlSearchTerm = searchParams.get('search');
    console.log('URL search parameter:', urlSearchTerm); // Debug log
    if (urlSearchTerm && urlSearchTerm !== searchTerm) {
      console.log('Setting search term from URL:', urlSearchTerm); // Debug log
      setSearchTerm(urlSearchTerm);
      setShowSearchResults(true);
    }
  }, [searchParams]); // Removed searchTerm dependency to avoid circular updates
  
  // Alternative: Read URL search on mount (backup method)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlSearch = urlParams.get('search');
    if (urlSearch && !searchTerm) {
      console.log('Backup: Setting search term from URL:', urlSearch);
      setSearchTerm(urlSearch);
      setShowSearchResults(true);
    }
  }, []); // Run once on mount

  // Update URL when search term changes (but not if it's from URL parameter loading)
  useEffect(() => {
    const currentUrlSearch = searchParams.get('search');
    if (searchTerm && searchTerm !== currentUrlSearch) {
      setSearchParams({ search: searchTerm });
      setShowSearchResults(true);
      setMaxVisibleResults(12);
    } else if (!searchTerm && currentUrlSearch) {
      setSearchParams({});
      setShowSearchResults(false);
      setMaxVisibleResults(12);
    }
  }, [searchTerm]); // Only depend on searchTerm, not searchParams to avoid circular updates

  // Fetch items from API - using optimized endpoint or private tag endpoint
  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoading(true);
        
        // If in private tag mode AND showing traveling items, fetch items by tag
        // BUT if showing stationary (Location Guestbook), fetch ALL stationary items from normal endpoint
        if (privateTagMode && initialPrivateTag && itemTypeFilter !== 'stationary') {
          console.log(`🏷️ Fetching traveling items by private tag: ${initialPrivateTag}`);
          const response = await fetch(
            `${API_BASE}/api/items/by-private-tag/${encodeURIComponent(initialPrivateTag)}`
          );
          const data = await response.json();
          if (data.success) {
            setItems(data.items || []);
            console.log(`✅ Loaded ${data.count} items with privateTag "${initialPrivateTag}"`);
          } else {
            console.error('Failed to load items by private tag:', data.error);
            setItems([]);
          }
          setError(null);
          setLoading(false);
          return;
        }
        
        // For Location Guestbook on club pages (or normal mode), fetch ALL items
        if (privateTagMode && initialPrivateTag && itemTypeFilter === 'stationary') {
          console.log(`🏠 Club page switching to Location Guestbook - fetching ALL stationary locations`);
        }
        
        // Normal mode: Fetch map items immediately, then optionally refine with geolocation
        // Start with a fast default load (no location) so the map appears quickly
        // If starting in stationary mode (guestbook page), request more stationary items
        const stationaryLimit = itemTypeFilter === 'stationary' ? 500 : 0;
        const travelingLimit = itemTypeFilter === 'stationary' ? 0 : 500;
        const defaultResponse = await fetch(
          `${API_BASE}/api/map-items?limit_stationary=${stationaryLimit}&limit_traveling=${travelingLimit}`
        );
        const defaultData = await defaultResponse.json();
        setItems(defaultData.items || []);
        console.log(`✅ Fast-loaded ${defaultData.count} items (no geolocation wait)`);
        setError(null);
        setLoading(false);
        
        // Then try to get geolocation in the background to refine results
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;
              setUserLocation([latitude, longitude]);
              
              // Refetch with user location for better nearby results
              try {
                const response = await fetch(
                  `${API_BASE}/api/map-items?user_lat=${latitude}&user_lng=${longitude}&limit_stationary=${stationaryLimit}&limit_traveling=${travelingLimit}&radius_miles=200`
                );
                const data = await response.json();
                setItems(data.items || []);
                console.log(`✅ Refined to ${data.count} items with user location`);
                
                // If in stationary/guestbook mode, queue a zoom to user's location
                if (itemTypeFilter === 'stationary') {
                  setPendingGeoZoom({ lat: latitude, lng: longitude });
                }
              } catch (err) {
                console.error('Error refining with location:', err);
              }
            },
            (error) => {
              console.log('Location access denied, using default items');
            },
            { timeout: 5000 }
          );
        }
      } catch (error) {
        console.error('Error fetching items:', error);
        setError('Failed to load items');
        setLoading(false);
      }
    };
    
    // Fetch stats in parallel for faster loading
    const fetchStats = async () => {
      console.log('🔄 fetchStats called');
      setStatsLoading(true);
      
      // Fetch basic stats and engagement stats in parallel (non-blocking)
      // When rendering inside a club page (`privateTagMode`), scope the Live
      // Statistics strip to that club only.
      const statsScope = privateTagMode && initialPrivateTag ? initialPrivateTag : null;
      const statsPromise = api.getStats(statsScope).then(data => {
        if (data && typeof data === 'object') {
          setBasicStats(data);
          console.log('✅ basicStats set successfully');
        }
      }).catch(err => console.error('❌ Error fetching basic stats:', err));

      const engagementPromise = api.getEngagementStats().then(data => {
        if (data && typeof data === 'object') {
          setEngagementStats(data);
          console.log('✅ engagementStats set successfully');
        }
      }).catch(err => console.error('❌ Error fetching engagement stats:', err));

      await Promise.allSettled([statsPromise, engagementPromise]);
      
      setStatsLoading(false);
    };

    fetchItems();
    fetchStats();
  }, [privateTagMode, initialPrivateTag, itemTypeFilter]);

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    try {
      // In privateTagMode, we already fetched the filtered items, so no need to filter again
      if (privateTagMode && initialPrivateTag) {
        console.log(`🏷️ Private tag mode - showing all ${items.length} items for tag "${initialPrivateTag}"`);
        return items;
      }
      
      if (!searchTerm || !items.length) return items;
      
      console.log(`🔍 Filtering ${items.length} items for searchTerm: "${searchTerm}"`);
      
      const results = items.filter(item => {
        try {
          const matchesName = (item?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
          const matchesCreator = (item?.creatorName || '').toLowerCase().includes(searchTerm.toLowerCase());
          const matchesLocation = (item?.currentLocation || '').toLowerCase().includes(searchTerm.toLowerCase());
          const matchesTag = (item?.privateTag || '').toLowerCase().includes(searchTerm.toLowerCase());
          const matchesStory = (item?.stories || []).some(story => {
            try {
              return (
                (story?.location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (story?.user || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                (story?.story || story?.content || '').toLowerCase().includes(searchTerm.toLowerCase())
              );
            } catch (storyError) {
              console.warn('Error filtering story:', storyError);
              return false;
            }
          });
          
          const matches = matchesName || matchesCreator || matchesLocation || matchesTag || matchesStory;
          
          // Debug: Log items that match on privateTag
          if (matchesTag) {
            console.log(`✅ Item ${item.uid} matches on privateTag: "${item.privateTag}"`);
          }
          
          return matches;
        } catch (itemError) {
          console.warn('Error filtering item:', itemError);
          return false;
        }
      });
      
      console.log(`🔍 Filter result: ${results.length} items match "${searchTerm}"`);
      return results;
    } catch (error) {
      console.error('Critical error in search filter:', error);
      return items; // Return all items if filter fails
    }
  }, [searchTerm, items]);

  // Helper function to apply filters to any item list
  const applyFilters = (itemsList) => {
    let results = [...itemsList];
    
    // Apply item type filter (traveling vs stationary)
    if (itemTypeFilter === 'traveling') {
      results = results.filter(item => item.itemType === 'traveling' || !item.itemType); // Default to traveling if not set
    } else if (itemTypeFilter === 'stationary') {
      results = results.filter(item => item.itemType === 'stationary');
    }
    // 'all' shows everything - no filter needed
    
    // Apply "My Items" filter (user's owned items and visited locations)
    if (showMyItemsOnly && currentUserEmail) {
      results = results.filter(item => {
        // Show items owned by user
        const isOwned = item.ownerEmail?.toLowerCase() === currentUserEmail.toLowerCase() || 
                       item.creatorEmail?.toLowerCase() === currentUserEmail.toLowerCase();
        
        // Show items where user has added a story
        const hasUserStory = item.stories?.some(story => 
          story.userEmail?.toLowerCase() === currentUserEmail.toLowerCase() ||
          story.contributorEmail?.toLowerCase() === currentUserEmail.toLowerCase()
        );
        
        return isOwned || hasUserStory;
      });
    }
    
    // Apply other filters
    if (filters.hasStories) {
      results = results.filter(item => (item.stories?.length || 0) > 0);
    }
    
    if (filters.hasTravel) {
      results = results.filter(item => {
        const miles = parseFloat((item.distance || '0').replace(/[^\d.]/g, '')) || 0;
        return miles > 0;
      });
    }
    
    if (filters.minDistance > 0) {
      results = results.filter(item => {
        const miles = parseFloat((item.distance || '0').replace(/[^\d.]/g, '')) || 0;
        return miles >= filters.minDistance;
      });
    }
    
    if (filters.recentActivity) {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      results = results.filter(item => {
        const lastStoryDate = item.stories?.[item.stories.length - 1]?.date;
        if (!lastStoryDate) return false;
        return new Date(lastStoryDate) >= thirtyDaysAgo;
      });
    }
    
    return results;
  };

  // Items to display on map - applies filters with or without search
  const mapDisplayItems = useMemo(() => {
    // Start with search results if searching, otherwise all items
    const baseItems = searchTerm ? filteredItems : items;
    
    // Apply filters
    let results = applyFilters(baseItems);
    
    // De-duplicate bulk-registered items (same name + story + image)
    const seenContent = new Set();
    results = results.filter(item => {
      const latestStory = item.stories?.[item.stories.length - 1];
      if (!latestStory) return true;
      const storyText = (latestStory.story || latestStory.content || '').trim().toLowerCase().substring(0, 100);
      const storyImage = latestStory.imageUrl || latestStory.image || '';
      const key = `${(item.name || '').toLowerCase()}|${storyText}|${storyImage}`;
      if (storyText.length > 0 && seenContent.has(key)) return false;
      seenContent.add(key);
      return true;
    });
    
    return results;
  }, [items, filteredItems, searchTerm, filters, itemTypeFilter]);

  // Enhanced search results with sorting, filtering, and pagination
  const searchResults = useMemo(() => {
    if (!searchTerm) return [];
    
    let results = applyFilters(filteredItems);
    
    // De-duplicate items from bulk registration sessions
    // Bulk items share the same name, story text, and image
    const seenContent = new Set();
    results = results.filter(item => {
      const latestStory = item.stories?.[item.stories.length - 1];
      if (!latestStory) return true; // keep items with no stories
      const storyText = (latestStory.story || latestStory.content || '').trim().toLowerCase().substring(0, 100);
      const storyImage = latestStory.imageUrl || latestStory.image || '';
      const key = `${(item.name || '').toLowerCase()}|${storyText}|${storyImage}`;
      if (storyText.length > 0 && seenContent.has(key)) return false;
      seenContent.add(key);
      return true;
    });
    
    // Sort results based on selected option
    switch (sortBy) {
      case 'recent':
        results.sort((a, b) => {
          const dateA = a.stories?.[a.stories.length - 1]?.date || '2024-01-01';
          const dateB = b.stories?.[b.stories.length - 1]?.date || '2024-01-01';
          return new Date(dateB) - new Date(dateA);
        });
        break;
      case 'stories':
        results.sort((a, b) => (b.stories?.length || 0) - (a.stories?.length || 0));
        break;
      case 'distance':
        results.sort((a, b) => {
          const milesA = parseFloat((a.distance || '0').replace(/[^\d.]/g, '')) || 0;
          const milesB = parseFloat((b.distance || '0').replace(/[^\d.]/g, '')) || 0;
          return milesB - milesA;
        });
        break;
      case 'relevance':
      default:
        // Sort by relevance: items with stories/images first, then by story count
        results.sort((a, b) => {
          const aStories = a.stories?.length || 0;
          const bStories = b.stories?.length || 0;
          const aHasImage = (a.initialImageUrl || a.locationPhotoUrl || a.stories?.find(s => s.image)) ? 1 : 0;
          const bHasImage = (b.initialImageUrl || b.locationPhotoUrl || b.stories?.find(s => s.image)) ? 1 : 0;
          const aHasName = a.name ? 1 : 0;
          const bHasName = b.name ? 1 : 0;
          // Named items first, then by story count, then by image
          if (aHasName !== bHasName) return bHasName - aHasName;
          if (aStories !== bStories) return bStories - aStories;
          return bHasImage - aHasImage;
        });
        break;
    }
    
    return results;
  }, [filteredItems, sortBy, searchTerm, filters, itemTypeFilter]);

  // Get visible results based on pagination
  const visibleSearchResults = useMemo(() => {
    return searchResults.slice(0, maxVisibleResults);
  }, [searchResults, maxVisibleResults]);

  // Search result statistics
  const searchStats = useMemo(() => {
    if (!searchTerm) return null;
    
    const locations = new Set();
    let totalStories = 0;
    
    searchResults.forEach(item => {
      if (item.currentLocation) locations.add(item.currentLocation);
      totalStories += item.stories?.length || 0;
    });
    
    return {
      totalItems: searchResults.length,
      uniqueLocations: locations.size,
      totalStories: totalStories
    };
  }, [searchResults, searchTerm]);

  // Function to copy search URL to clipboard
  const copySearchURL = async () => {
    try {
      // Construct URL with search parameter
      const baseURL = window.location.origin + window.location.pathname;
      const searchURL = searchTerm ? `${baseURL}?search=${encodeURIComponent(searchTerm)}` : baseURL;
      
      await navigator.clipboard.writeText(searchURL);
      
      // Show success notification
      alert(`✅ Search link copied!\n\nShare this link to show results for: "${searchTerm}"`);
      console.log('Search URL copied to clipboard:', searchURL);
    } catch (err) {
      console.error('Failed to copy search URL:', err);
      // Fallback for older browsers
      const baseURL = window.location.origin + window.location.pathname;
      const searchURL = searchTerm ? `${baseURL}?search=${encodeURIComponent(searchTerm)}` : baseURL;
      const textArea = document.createElement('textarea');
      textArea.value = searchURL;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert(`✅ Search link copied!\n\nShare this link to show results for: "${searchTerm}"`);
    }
  };

  // Calculate featured items for sidebar - now works with search results too
  const featuredItems = useMemo(() => {
    // Use search results if searching, otherwise use all items
    let sourceItems = searchTerm ? searchResults : items;
    
    // Apply item type filter to featured items
    if (itemTypeFilter === 'traveling') {
      sourceItems = sourceItems.filter(item => item.itemType === 'traveling' || !item.itemType);
    } else if (itemTypeFilter === 'stationary') {
      sourceItems = sourceItems.filter(item => item.itemType === 'stationary');
    }
    
    if (!sourceItems.length) return [];
    
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    // Helper function to parse distance string to miles
    const parseDistance = (distanceStr) => {
      if (!distanceStr || typeof distanceStr !== 'string') return 0;
      const match = distanceStr.match(/^([\d,]+(?:\.\d+)?)/);
      if (match) {
        return parseFloat(match[1].replace(/,/g, ''));
      }
      return 0;
    };
    
    // Helper function to get recent stories (last 30 days)
    const getRecentStories = (item) => {
      const stories = item.stories || [];
      return stories.filter(story => {
        const storyDate = new Date(story.date);
        return storyDate >= thirtyDaysAgo;
      });
    };
    
    // 1. Most miles in past 30 days (based on story distance_miles within 30 days)
    const getRecentDistance = (item) => {
      const stories = item.stories || [];
      return stories.reduce((total, story) => {
        const storyDate = new Date(story.date);
        if (storyDate >= thirtyDaysAgo) {
          return total + (story.distance_miles || 0);
        }
        return total;
      }, 0);
    };
    
    const mostMilesList = [...sourceItems].sort((a, b) => {
      const recentMilesA = getRecentDistance(a);
      const recentMilesB = getRecentDistance(b);
      // If same recent distance, fall back to total distance
      if (recentMilesA === recentMilesB) {
        return parseDistance(b.distance || '0') - parseDistance(a.distance || '0');
      }
      return recentMilesB - recentMilesA;
    });
    
    // 2. Most stories in past 30 days (approximation based on total stories) - sorted list
    const mostStoriesList = [...sourceItems].sort((a, b) => {
      const recentStoriesA = getRecentStories(a).length;
      const recentStoriesB = getRecentStories(b).length;
      // If same recent stories, fall back to total stories
      if (recentStoriesA === recentStoriesB) {
        return (b.stories?.length || 0) - (a.stories?.length || 0);
      }
      return recentStoriesB - recentStoriesA;
    });
    
    // 3. Most recent story (latest story across all items) - sorted list
    const mostRecentList = [...sourceItems].sort((a, b) => {
      const latestA = a.stories?.[a.stories.length - 1]?.date || '2024-01-01';
      const latestB = b.stories?.[b.stories.length - 1]?.date || '2024-01-01';
      return new Date(latestB) - new Date(latestA);
    });
    
    // Create featured items with categories, avoiding duplicates
    const featured = [];
    const addedItems = new Set();
    
    // Priority order changed to ensure "Most stories in past 30 days" is shown
    // 1. Add most stories in past 30 days (highest priority since it's user's concern)
    for (const item of mostStoriesList) {
      if (!addedItems.has(item.uid)) {
        const recentCount = getRecentStories(item).length;
        if (recentCount > 0) {  // Only show if there are actually recent stories
          featured.push({ 
            ...item, 
            category: searchTerm ? 'Most Stories (in results)' : 'Most Stories (past 30 days)', 
            icon: '📚' 
          });
          addedItems.add(item.uid);
          break;
        }
      }
    }
    
    // 2. Add most miles item (if not already added)
    for (const item of mostMilesList) {
      if (!addedItems.has(item.uid)) {
        const recentMiles = getRecentDistance(item);
        if (recentMiles > 0) {  // Only show if there's distance in past 30 days
          featured.push({ 
            ...item, 
            category: searchTerm ? 'Greatest Distance (in results)' : 'Most Distance Traveled (past 30 days)', 
            icon: '🚀' 
          });
          addedItems.add(item.uid);
          break;
        }
      }
    }
    
    // 3. Add most recent item (if not already added)
    for (const item of mostRecentList) {
      if (!addedItems.has(item.uid)) {
        featured.push({ 
          ...item, 
          category: searchTerm ? 'Latest Update (in results)' : 'Latest Update', 
          icon: '🕒' 
        });
        addedItems.add(item.uid);
        break;
      }
    }
    
    // 4. If we still don't have 3 items, add items with most total stories
    if (featured.length < 3) {
      const mostTotalStoriesList = [...sourceItems].sort((a, b) => (b.stories?.length || 0) - (a.stories?.length || 0));
      for (const item of mostTotalStoriesList) {
        if (!addedItems.has(item.uid) && featured.length < 3) {
          featured.push({ 
            ...item, 
            category: searchTerm ? 'Most Stories (in results)' : 'Most Total Stories', 
            icon: '📖' 
          });
          addedItems.add(item.uid);
        }
      }
    }
    
    return featured;
  }, [items, searchTerm, searchResults, itemTypeFilter]);

  // Use featured items for sidebar display (now filtered by search when applicable)
  const sidebarItems = showAllItems ? items : featuredItems;

  const handleToggleShowAll = () => {
    setShowAllItems(!showAllItems);
    setSelectedItem(null); // Clear selection when toggling
  };

  // Get all markers and paths - show filtered/searched items on map
  const mapData = useMemo(() => {
    const markers = [];
    const paths = [];
    
    // Use filtered items for map display (includes search + filters)
    const mapItems = mapDisplayItems;
    
    mapItems.forEach(item => {
      const itemPaths = [];
      
      // Ensure item has stories array
      const stories = item.stories || [];
      
      stories.forEach((story, index) => {
        // Skip stories without coordinates
        if (!story.coordinates || !story.coordinates.lat || !story.coordinates.lng) {
          return;
        }
        
        const isStart = index === 0;
        const isCurrent = index === stories.length - 1;
        
        markers.push({
          id: `${item.uid}-${index}`,
          itemId: item.uid,
          position: [story.coordinates.lat, story.coordinates.lng],
          story,
          item,
          isStart,
          isCurrent,
          storyIndex: index,
          itemType: item.itemType || 'traveling' // NEW: Include item type for icon selection
        });
        
        itemPaths.push([story.coordinates.lat, story.coordinates.lng]);
      });
      
      if (itemPaths.length > 1) {
        paths.push({
          itemId: item.uid,
          itemName: item.name,
          path: itemPaths,
          color: item.uid === 'TM001234' ? '#10b981' : item.uid === 'TM005678' ? '#3b82f6' : '#8b5cf6'
        });
      }
    });
    
    return { markers, paths };
  }, [mapDisplayItems]);

  const handleItemClick = (item) => {
    setSelectedItem(selectedItem?.id === item.id ? null : item);
  };

  const handleViewJourney = (item) => {
    try {
      if (item && item.uid) {
        navigate(`/item/${item.uid}`);
      } else {
        console.error('Invalid item for navigation:', item);
      }
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  return (
    <section className="py-12 bg-slate-100">
      <div className="container mx-auto px-4">
        {/* Search and Filters - moved above map section */}
        
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme?.primaryColor || '#10b981' }} />
            <span className="ml-2 text-gray-600">Loading items...</span>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()}>Try Again</Button>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto">
            {/* Live Statistics Section - Mobile Optimized */}
            <div className="mb-4 md:mb-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div 
                className="px-4 md:px-6 py-2 md:py-4 cursor-pointer md:cursor-default"
                style={{ 
                  background: theme?.gradientStyle || 'linear-gradient(to right, #1b4332, #2d6a4f, #1b4332)'
                }}
                onClick={() => setStatsExpanded(!statsExpanded)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-white text-base md:text-xl font-bold">Live Statistics</h3>
                  <div className="md:hidden">
                    {statsExpanded ? (
                      <ChevronUp className="w-5 h-5 text-white" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-white" />
                    )}
                  </div>
                </div>
                {/* Mobile Quick Stats - Always Visible */}
                <div className="md:hidden mt-2 flex justify-center space-x-4">
                  <div className="text-center">
                    <div className="text-lg font-bold text-white">
                      {basicStats?.itemsScannedAllTime?.toLocaleString() || 0}
                    </div>
                    <div className="text-xs text-white/80">Scans</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-white">
                      {basicStats?.travelingItems?.toLocaleString() || 0}
                    </div>
                    <div className="text-xs text-white/80">Traveling</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-white">
                      {basicStats?.stationaryLocations?.toLocaleString() || 0}
                    </div>
                    <div className="text-xs text-white/80">Locations</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-bold text-white">
                      {basicStats?.milesTracked?.toLocaleString() || 0}
                    </div>
                    <div className="text-xs text-white/80">Miles</div>
                  </div>
                </div>
              </div>
              
              {/* Detailed Stats - Collapsible on Mobile */}
              <div className={`${statsExpanded ? 'block' : 'hidden'} md:block p-3 md:p-4 overflow-hidden`}>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 min-w-0">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {basicStats?.itemsScannedAllTime?.toLocaleString() || 0}
                    </div>
                    <div className="flex items-center justify-center text-sm text-gray-600 font-medium">
                      <QrCode className="w-4 h-4 mr-1" style={{ color: theme?.iconColor || '#2d6a4f' }} />
                      Total Scans
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-2xl font-bold mb-1" style={{ color: theme?.secondaryColor || '#b8860b' }}>
                      {basicStats?.travelingItems?.toLocaleString() || 0}
                    </div>
                    <div className="flex items-center justify-center text-sm text-gray-600 font-medium">
                      <Package className="w-4 h-4 mr-1" style={{ color: theme?.secondaryColor || '#b8860b' }} />
                      Traveling Items
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-2xl font-bold mb-1" style={{ color: theme?.primaryColor || '#2d6a4f' }}>
                      {basicStats?.stationaryLocations?.toLocaleString() || 0}
                    </div>
                    <div className="flex items-center justify-center text-sm text-gray-600 font-medium">
                      <MapPin className="w-4 h-4 mr-1" style={{ color: theme?.primaryColor || '#2d6a4f' }} />
                      Locations to Visit
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {basicStats?.milesTracked?.toLocaleString() || 0}
                    </div>
                    <div className="flex items-center justify-center text-sm text-gray-600 font-medium">
                      <MapPin className="w-4 h-4 text-amber-700 mr-1" />
                      Miles Traveled
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      {basicStats?.totalStories?.toLocaleString() || 0}
                    </div>
                    <div className="flex items-center justify-center text-sm text-gray-600 font-medium">
                      <Camera className="w-4 h-4 text-amber-700 mr-1" />
                      Stories Shared
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Map and Sidebar Layout */}
            <div className="grid lg:grid-cols-3 gap-4 md:gap-8 overflow-hidden">
              {/* Map Section with Search */}
              <div className="lg:col-span-2 space-y-4">
                {/* Search and Filters - positioned above map */}
                <div className="bg-white rounded-lg p-3 md:p-4 shadow-lg border border-gray-200">
                  <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-center">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        type="text"
                        placeholder="Search items, locations, or creators..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') commitSearch(); }}
                        className="pl-10 pr-10 py-2 text-base border-2 border-gray-200 focus:border-emerald-500 rounded-lg"
                        data-testid="map-search-input"
                      />
                      {searchInput && (
                        <button
                          onClick={clearSearch}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          title="Clear search"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    
                    {/* Search button - triggers search on click */}
                    <Button 
                      onClick={commitSearch}
                      disabled={!searchInput}
                      className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white"
                      data-testid="map-search-btn"
                    >
                      <Search className="w-4 h-4 mr-2" />
                      Search
                    </Button>
                    
                    {/* Sort dropdown - only show when searching */}
                    {searchTerm && (
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-emerald-500"
                      >
                        <option value="relevance">Sort by Relevance</option>
                        <option value="recent">Most Recent</option>
                        <option value="stories">Most Stories</option>
                        <option value="distance">Most Distance</option>
                      </select>
                    )}
                    
                    {/* Share search button - only show when searching */}
                    {searchTerm && (
                      <Button 
                        variant="outline" 
                        onClick={copySearchURL}
                        className="px-4 py-2 border-2 border-gray-200 hover:border-emerald-500"
                        title="Copy search link to share"
                      >
                        <Share2 className="w-4 h-4 mr-2" />
                        Share Search
                      </Button>
                    )}
                    
                    <Button 
                      variant="outline" 
                      className="px-4 py-2 border-2 border-gray-200 hover:border-emerald-500 relative"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      Filters
                      {(filters.hasStories || filters.hasTravel || filters.minDistance > 0 || filters.recentActivity) && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full"></span>
                      )}
                    </Button>
                  </div>
                  
                  {/* Filters Panel */}
                  {showFilters && (
                    <Card className="mt-3 p-4 bg-gray-50 border-2 border-gray-200">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-900">Filter Results</h3>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFilters({
                              hasStories: false,
                              hasTravel: false,
                              minDistance: 0,
                              recentActivity: false
                            })}
                            className="text-xs text-emerald-600 hover:text-emerald-700"
                          >
                            Clear All
                          </Button>
                        </div>
                        
                        {/* Filter checkboxes */}
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filters.hasStories}
                            onChange={(e) => setFilters({...filters, hasStories: e.target.checked})}
                            className="w-4 h-4 text-emerald-600 rounded"
                          />
                          <span className="text-sm text-gray-700">Has Stories</span>
                        </label>
                        
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filters.hasTravel}
                            onChange={(e) => setFilters({...filters, hasTravel: e.target.checked})}
                            className="w-4 h-4 text-emerald-600 rounded"
                          />
                          <span className="text-sm text-gray-700">Has Traveled</span>
                        </label>
                        
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filters.recentActivity}
                            onChange={(e) => setFilters({...filters, recentActivity: e.target.checked})}
                            className="w-4 h-4 text-emerald-600 rounded"
                          />
                          <span className="text-sm text-gray-700">Recent Activity (Last 30 Days)</span>
                        </label>
                        
                        {/* Min distance slider */}
                        <div className="pt-2">
                          <label className="text-sm text-gray-700 block mb-2">
                            Minimum Distance: {filters.minDistance} miles
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="1000"
                            step="50"
                            value={filters.minDistance}
                            onChange={(e) => setFilters({...filters, minDistance: parseInt(e.target.value)})}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                          />
                        </div>
                      </div>
                    </Card>
                  )}
                  
                  {/* Search results info */}
                  {searchTerm && (
                    <div className="mt-3 flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="secondary" className="text-sm">
                          Found {searchStats?.totalItems || 0} item{(searchStats?.totalItems || 0) !== 1 ? 's' : ''}
                        </Badge>
                        {searchStats && (
                          <>
                            <Badge variant="outline" className="text-xs">
                              {searchStats.uniqueLocations} location{searchStats.uniqueLocations !== 1 ? 's' : ''}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {searchStats.totalStories} stor{searchStats.totalStories !== 1 ? 'ies' : 'y'}
                            </Badge>
                          </>
                        )}
                      </div>
                      
                      {/* Show more button */}
                      {searchResults.length > maxVisibleResults && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setMaxVisibleResults(prev => prev + 10)}
                          className="text-emerald-600 hover:text-emerald-700"
                        >
                          Show {Math.min(10, searchResults.length - maxVisibleResults)} more
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* My Items Filter - Only show if user is logged in */}
                {currentUserEmail && (
                  <div className="flex justify-center mb-3">
                    <label className="flex items-center gap-2 cursor-pointer bg-white px-4 py-2 rounded-lg border border-gray-200 hover:border-emerald-300 transition-colors">
                      <input
                        type="checkbox"
                        checked={showMyItemsOnly}
                        onChange={(e) => setShowMyItemsOnly(e.target.checked)}
                        className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Show Only My Items & Visited Locations
                      </span>
                    </label>
                  </div>
                )}

                {/* Item Type Toggle - Show Traveling vs Stationary */}
                <div className="flex flex-wrap justify-center gap-2 mb-4">
                  {/* Auto-toggle indicator */}
                  {autoToggleEnabled && (
                    <div className="w-full text-center mb-2">
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                        ✨ Auto-showcasing {itemTypeFilter === 'traveling' ? 'Traveling Items' : 'Guestbook Locations'}
                      </span>
                    </div>
                  )}
                  <button
                    onClick={() => handleManualFilterSelect('all')}
                    className={`px-3 py-1.5 rounded-lg font-medium transition-all text-xs sm:text-sm whitespace-nowrap ${
                      itemTypeFilter === 'all'
                        ? 'bg-gray-900 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All Items
                  </button>
                  <button
                    onClick={() => handleManualFilterSelect('traveling')}
                    style={itemTypeFilter === 'traveling' ? { backgroundColor: theme?.secondaryColor || '#3b82f6' } : {}}
                    className={`px-3 py-1.5 rounded-lg font-medium transition-all flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap ${
                      itemTypeFilter === 'traveling'
                        ? 'text-white shadow-md'
                        : 'bg-amber-50 text-amber-800 hover:bg-amber-100'
                    }`}
                  >
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="hidden sm:inline">Traveling Items</span>
                    <span className="inline sm:hidden">Traveling</span>
                  </button>
                  <button
                    onClick={() => {
                      handleManualFilterSelect('stationary');
                      // Also trigger the location guestbook click behavior
                      if (navigator.geolocation) {
                        navigator.geolocation.getCurrentPosition(
                          (position) => {
                            const newLocation = {
                              lat: position.coords.latitude,
                              lng: position.coords.longitude
                            };
                            setUserLocation(newLocation);
                            setShouldZoomToUser(true);
                          },
                          () => {
                            // Geolocation failed, just switch filter
                          }
                        );
                      }
                    }}
                    style={itemTypeFilter === 'stationary' ? { backgroundColor: theme?.primaryColor || '#10b981' } : {}}
                    className={`px-3 py-1.5 rounded-lg font-medium transition-all flex items-center gap-1.5 text-xs sm:text-sm whitespace-nowrap ${
                      itemTypeFilter === 'stationary'
                        ? 'text-white shadow-md'
                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                    }`}
                  >
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="hidden sm:inline">Location Guest Book</span>
                    <span className="inline sm:hidden">Guest Book</span>
                  </button>
                </div>

                {/* Map Container - responsive height for mobile optimization */}
                <Card className="shadow-xl border-2 border-gray-100 h-[250px] md:h-[378px] max-h-[250px] md:max-h-[378px] w-full min-w-0">
                  <div className="h-full relative overflow-hidden">
                    {/* Map Interaction Hint - shown until user clicks zoom controls */}
                    {!mapInteractionEnabled && (
                      <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 z-20 bg-gray-800/80 text-white text-xs px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2">
                        <span>👆</span>
                        <span>Use +/- to interact with map</span>
                      </div>
                    )}
                    
                    {/* Mobile Map Info Banner */}
                    {isMobile && (
                      <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-20 bg-emerald-700 text-white text-xs px-3 py-1 rounded-full shadow-lg">
                        📍 Showing items near you
                      </div>
                    )}
                    
                    {/* Search This Area Button - positioned at top center */}
                    {showSearchButton && !loadingMoreItems && (
                      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
                        <Button
                          onClick={handleSearchThisArea}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg px-4 py-2 flex items-center gap-2"
                        >
                          <Search className="w-4 h-4" />
                          Search This Area
                        </Button>
                      </div>
                    )}
                    
                    {/* Loading indicator */}
                    {loadingMoreItems && (
                      <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
                        <div className="bg-white shadow-lg px-4 py-2 rounded-lg flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                          <span className="text-sm text-gray-700">Loading items...</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Home Button - positioned in lower left */}
                    <div className="absolute bottom-4 left-4 z-10">
                      <Button
                        onClick={handleHomeClick}
                        className="bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-300 shadow-lg rounded-full w-10 h-10 p-0 flex items-center justify-center"
                        title="Center map on your location"
                      >
                        <Home className="w-4 h-4" />
                      </Button>
                    </div>

                    <MapContainer
                      center={[39.8283, -98.5795]} // Center on USA by default
                      zoom={isMobile ? 3 : 4} // Balanced overview of United States
                      style={{ 
                        height: '100%', 
                        width: '100%', 
                        maxHeight: '100%',
                        zIndex: 1
                      }}
                      className="z-0 map-container-overflow"
                      whenCreated={setMapRef}
                      scrollWheelZoom={false} // Disabled by default
                      dragging={false} // Disabled by default - enables page scrolling
                      touchZoom={false} // Disabled by default - enables page scrolling on mobile
                      doubleClickZoom={false} // Disabled by default
                      zoomControl={true} // Show zoom controls - clicking these enables interactions
                    >
                      <MapEvents />
                      <AutoZoomToLocation />
                      <MapInteractionHandler />
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    
                    {/* Draw paths */}
                    {mapData.paths.map((pathData) => (
                      <Polyline
                        key={pathData.itemId}
                        positions={pathData.path}
                        color={pathData.color}
                        weight={2}
                        opacity={0.8}
                      />
                    ))}
                    
                    {/* Draw individual markers without clustering */}
                    {mapData.markers.map((marker) => {
                      // Choose icon based on item type
                      let markerIcon;
                      if (marker.itemType === 'stationary') {
                        // Stationary items use pin-style markers
                        markerIcon = stationaryIcon;
                      } else {
                        // Traveling items use the original logic
                        markerIcon = marker.isStart ? startIcon : marker.isCurrent ? currentIcon : visitedIcon;
                      }
                      
                      // Get item details for tooltip
                      const item = marker.item || {};
                      const story = marker.story || {};
                      const itemName = item.name || 'Unknown Location';
                      // Use the story's location for THIS marker, not the item's current/start location
                      const storyLocation = story.location || item.currentLocation || '';
                      const itemType = marker.itemType === 'stationary' ? 'Location' : 'Traveling Item';
                      
                      // Format date if available
                      const storyDate = story.timestamp || story.date;
                      let formattedDate = '';
                      if (storyDate) {
                        try {
                          formattedDate = new Date(storyDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          });
                        } catch (e) {}
                      }
                      
                      // Determine marker type label
                      let markerLabel = '';
                      if (marker.isCurrent) {
                        markerLabel = '📍 Current Location';
                      } else if (marker.isStart) {
                        markerLabel = '🚀 Start Location';
                      } else {
                        markerLabel = '📌 Visited';
                      }
                      
                      // Create tooltip content with story-specific location
                      const tooltipContent = (
                        <div style={{ 
                          maxWidth: '200px', 
                          padding: '4px',
                          fontSize: '13px',
                          lineHeight: '1.4'
                        }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                            {itemName}
                          </div>
                          {storyLocation && (
                            <div style={{ 
                              color: '#374151', 
                              fontSize: '12px',
                              marginBottom: '4px'
                            }}>
                              📍 {storyLocation}
                            </div>
                          )}
                          {formattedDate && (
                            <div style={{ 
                              color: '#6b7280', 
                              fontSize: '11px',
                              marginBottom: '4px'
                            }}>
                              📅 {formattedDate}
                            </div>
                          )}
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#999', 
                            marginTop: '4px',
                            fontStyle: 'italic'
                          }}>
                            {markerLabel} • Click for details
                          </div>
                        </div>
                      );
                      
                      return (
                        <Marker
                          key={marker.id}
                          position={marker.position}
                          icon={markerIcon}
                          eventHandlers={{
                            click: () => {
                              // Show item details in Featured Items section
                              // Include the specific story that was clicked
                              setSelectedMarkerItem({
                                ...marker.item,
                                selectedStory: marker.story,
                                selectedStoryIndex: marker.storyIndex,
                                isStartMarker: marker.isStart,
                                isCurrentMarker: marker.isCurrent
                              });
                              // Scroll to Featured Items section
                              if (!isMobile) {
                                document.querySelector('.featured-items-section')?.scrollIntoView({ 
                                  behavior: 'smooth', 
                                  block: 'nearest' 
                                });
                              }
                            }
                          }}
                        >
                          <Tooltip
                            direction="top"
                            offset={[0, -10]}
                            opacity={0.95}
                            permanent={false}
                          >
                            {tooltipContent}
                          </Tooltip>
                        </Marker>
                      );
                    })}
                  </MapContainer>
                </div>
                </Card>
                
                {/* Map Legend - Compact Single Line */}
                <div className="mt-4 bg-white rounded-lg px-3 py-2 shadow-lg">
                  <div className="flex gap-6 text-xs justify-center">
                    <div className="flex items-center whitespace-nowrap">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full mr-1 border border-white shadow-sm"></div>
                      <span className="text-gray-600">Start</span>
                    </div>
                    <div className="flex items-center whitespace-nowrap">
                      <div className="w-2 h-2 bg-emerald-600 rounded-full mr-1 border border-white shadow-sm"></div>
                      <span className="text-gray-600">Visited</span>
                    </div>
                    <div className="flex items-center whitespace-nowrap">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-1 border border-white shadow-sm"></div>
                      <span className="text-gray-600">Current</span>
                    </div>
                    <div className="flex items-center whitespace-nowrap">
                      <div className="w-4 h-0.5 bg-gray-500 mr-1"></div>
                      <span className="text-gray-600">Path</span>
                    </div>
                  </div>
                </div>

                {/* Monthly Leaderboard - Replaces Community Engagement */}
                <div className="mt-4">
                  <HomepageLeaderboard />
                </div>
              </div>
              {/* Featured Items Sidebar */}
              <div className="space-y-4 featured-items-section">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900">
                    {selectedMarkerItem ? 'Selected Location' :
                     searchTerm ? `Featured in "${searchTerm}"` : 
                     showAllItems ? `All Items (${sidebarItems.length})` : 'Featured Items'}
                  </h3>
                  {selectedMarkerItem ? (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSelectedMarkerItem(null)}
                      className="text-xs"
                    >
                      Close
                    </Button>
                  ) : !searchTerm && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleToggleShowAll}
                      className="text-xs"
                    >
                      {showAllItems ? 'Show Featured' : 'Show All'}
                    </Button>
                  )}
                  
                  {/* Copy search URL button in sidebar */}
                  {searchTerm && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={copySearchURL}
                      className="text-emerald-600 hover:text-emerald-700"
                      title="Copy search link"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                {/* Selected Marker Detail View */}
                {selectedMarkerItem && (
                  <Card className="overflow-hidden shadow-xl border-4 border-emerald-500 bg-emerald-50">
                    <CardContent className="p-6">
                      {/* Item/Story image - prioritize selected story's image */}
                      {(() => {
                        const storyImage = selectedMarkerItem.selectedStory?.imageUrl || selectedMarkerItem.selectedStory?.image;
                        const fallbackImage = selectedMarkerItem.locationPhotoUrl || selectedMarkerItem.initialImageUrl || 
                          (selectedMarkerItem.stories && selectedMarkerItem.stories.length > 0 && selectedMarkerItem.stories[selectedMarkerItem.stories.length - 1].imageUrl);
                        const imageToShow = storyImage || fallbackImage;
                        
                        return imageToShow ? (
                          <div className="mb-4">
                            <img 
                              src={imageToShow}
                              alt={selectedMarkerItem.name}
                              className="w-full h-48 object-cover rounded-lg"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          </div>
                        ) : null;
                      })()}
                      
                      {/* Item Type Badge */}
                      {selectedMarkerItem.itemType === 'stationary' && (
                        <Badge className="mb-2 bg-emerald-600 text-white">
                          📍 Location Guest Book
                        </Badge>
                      )}
                      
                      <h4 className="font-bold text-lg mb-2 text-gray-900">{selectedMarkerItem.name}</h4>
                      
                      {/* Show which location this is */}
                      {selectedMarkerItem.selectedStory && (
                        <div className="mb-2">
                          {selectedMarkerItem.isCurrentMarker && (
                            <Badge className="bg-red-500 text-white mr-2">📍 Current Location</Badge>
                          )}
                          {selectedMarkerItem.isStartMarker && !selectedMarkerItem.isCurrentMarker && (
                            <Badge className="bg-emerald-500 text-white mr-2">🚀 Start Location</Badge>
                          )}
                          {!selectedMarkerItem.isStartMarker && !selectedMarkerItem.isCurrentMarker && (
                            <Badge className="bg-emerald-700 text-white mr-2">Visited Location</Badge>
                          )}
                        </div>
                      )}
                      
                      <p className="text-sm text-gray-600 mb-2">
                        📍 {selectedMarkerItem.selectedStory?.location || selectedMarkerItem.currentLocation}
                      </p>
                      
                      {/* Date of this story */}
                      {selectedMarkerItem.selectedStory && (selectedMarkerItem.selectedStory.timestamp || selectedMarkerItem.selectedStory.date) && (
                        <p className="text-xs text-gray-500 mb-2">
                          📅 {new Date(selectedMarkerItem.selectedStory.timestamp || selectedMarkerItem.selectedStory.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      )}
                      
                      {/* Story preview - show the SELECTED story, not the first one */}
                      {selectedMarkerItem.selectedStory?.story && (
                        <p className="text-sm text-gray-700 mb-3 line-clamp-3">
                          {selectedMarkerItem.selectedStory.story}
                        </p>
                      )}
                      
                      {/* Stats */}
                      <div className="flex gap-4 mb-4 text-xs text-gray-600">
                        <span>📖 {selectedMarkerItem.stories?.length || 0} stories</span>
                        <span>👁️ {selectedMarkerItem.totalScans || 0} views</span>
                      </div>
                      
                      {/* Gamification Points for Stationary */}
                      {selectedMarkerItem.itemType === 'stationary' && (
                        <div className="mb-4 p-3 bg-gradient-to-r from-yellow-50 to-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">⭐</span>
                              <span className="font-semibold text-amber-700">Visit Points</span>
                            </div>
                            <span className="text-2xl font-bold text-amber-600">+10</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="space-y-2">
                        {/* Check In Button for Stationary Locations */}
                        {selectedMarkerItem.itemType === 'stationary' && (
                          <Button
                            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold"
                            onClick={() => handleViewJourney(selectedMarkerItem)}
                          >
                            ✨ Check In & Earn Points
                          </Button>
                        )}
                        
                        {/* Website Link if available */}
                        {selectedMarkerItem.website_url && (
                          <Button
                            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
                            onClick={() => window.open(selectedMarkerItem.website_url, '_blank')}
                          >
                            🌐 Visit Website
                          </Button>
                        )}
                        
                        {selectedMarkerItem.itemType === 'stationary' && selectedMarkerItem.coordinates && (
                          <Button
                            className="w-full bg-emerald-700 hover:bg-emerald-800 text-white"
                            onClick={() => {
                              const lat = selectedMarkerItem.coordinates.lat;
                              const lng = selectedMarkerItem.coordinates.lng;
                              window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
                            }}
                          >
                            🗺️ Get Directions
                          </Button>
                        )}
                        
                        <Button
                          className="w-full text-white"
                          style={{ backgroundColor: theme?.primaryColor || '#10b981' }}
                          onClick={() => handleViewJourney(selectedMarkerItem)}
                        >
                          📖 View All Stories
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {!selectedMarkerItem && sidebarItems.map((item) => (
                  <Card 
                    key={item.uid} 
                    className="overflow-hidden shadow-lg hover:shadow-xl transition-all cursor-pointer border-2 border-slate-200 hover:border-emerald-300 bg-white"
                  >
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        {/* Item Details */}
                        <div className="flex-1 min-w-0">
                          {/* Category Badge for Featured Items */}
                          {((!showAllItems && !searchTerm) || searchTerm) && item.category && (
                            <div className="mb-2">
                              <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs">
                                {item.icon} {item.category}
                              </Badge>
                            </div>
                          )}
                          
                          <h4 className="font-bold text-sm mb-1 text-gray-900 truncate">{item.name}</h4>
                          <p className="text-xs text-gray-600 mb-1">
                            📍 {item.currentLocation}
                            {item.stories && item.stories.length > 0 && (() => {
                              const storyDate = item.stories[item.stories.length - 1].date;
                              const dateObj = new Date(storyDate);
                              const isValidDate = !isNaN(dateObj.getTime());
                              return isValidDate ? (
                                <span> • {dateObj.toLocaleDateString()}</span>
                              ) : null;
                            })()}
                          </p>
                          <p className="text-xs text-gray-600 mb-2 flex items-center gap-1">
                            {item.itemType === 'stationary' ? (
                              <span className="flex items-center gap-1"><Camera className="w-3 h-3 text-amber-700" /> {item.stories?.length || 0} {item.stories?.length === 1 ? 'story' : 'stories'}</span>
                            ) : (
                              <span className="flex items-center gap-1">{item.distance} • <Camera className="w-3 h-3 text-amber-700" /> {item.stories?.length || 0} {item.stories?.length === 1 ? 'story' : 'stories'}</span>
                            )}
                          </p>
                          
                          {/* Latest Story Info - Compact */}
                          {item.stories && item.stories.length > 0 && (
                            <div className="mb-3 p-2 bg-gray-50 rounded text-xs">
                              <p className="text-gray-700 font-medium">
                                "{(item.stories[item.stories.length - 1].story || 'No story text').substring(0, 60)}..."
                              </p>
                              <p className="text-gray-500 mt-1 italic">
                                Latest story update
                              </p>
                            </div>
                          )}
                          
                          {/* Website Link */}
                          {item.website_url && (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(item.website_url, '_blank');
                              }}
                              size="sm"
                              variant="outline"
                              className="w-full mb-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            >
                              🌐 Visit Website
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </Button>
                          )}
                          
                          {/* Get Directions Button */}
                          {item.itemType === 'stationary' && item.coordinates && (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                const lat = item.coordinates.lat;
                                const lng = item.coordinates.lng;
                                window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
                              }}
                              size="sm"
                              variant="outline"
                              className="w-full mb-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            >
                              🗺️ Get Directions
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </Button>
                          )}
                          
                          <Button
                            onClick={() => handleViewJourney(item)}
                            size="sm"
                            className="w-full text-white"
                            style={{ backgroundColor: theme?.primaryColor || '#10b981' }}
                          >
                            View All Stories
                            <ExternalLink className="w-3 h-3 ml-1" />
                          </Button>
                        </div>
                        
                        {/* Story Thumbnail - Right Side - Always Show Container */}
                        <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                          {(() => {
                            // Walk stories newest → oldest and pick the first
                            // one with a photo — keeps the sidebar dynamic on
                            // every new story upload instead of showing the
                            // static initialImageUrl / locationPhotoUrl.
                            let mostRecentStoryImage = null;
                            if (item.stories) {
                              for (let i = item.stories.length - 1; i >= 0; i--) {
                                const s = item.stories[i];
                                const img = s?.image || s?.imageUrl;
                                if (img) { mostRecentStoryImage = img; break; }
                              }
                            }
                            const latestStory = item.stories && item.stories.length > 0 ? item.stories[item.stories.length - 1] : null;
                            const imageUrl = mostRecentStoryImage
                              || (item.itemType === 'stationary'
                                ? (item.locationPhotoUrl || item.initialImageUrl)
                                : (item.initialImageUrl || item.locationPhotoUrl));
                            
                            if (imageUrl) {
                              return (
                                <img 
                                  src={imageUrl} 
                                  alt={item.itemType === 'stationary' ? item.name : `Latest story from ${latestStory?.location || 'unknown location'}`}
                                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              );
                            } else {
                              return (
                                <div className="w-full h-full flex items-center justify-center text-gray-500">
                                  <Package className="w-6 h-6" />
                                </div>
                              );
                            }
                          })()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            
            {/* Comprehensive Search Results Section - Below Map */}
            {searchTerm && (
              <div className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                <div 
                  className="px-6 py-4"
                  style={{ 
                    background: theme?.gradientStyle || 'linear-gradient(to right, #3b82f6, #10b981)'
                  }}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-white text-xl font-bold">
                      All Search Results for "{searchTerm}" ({searchResults.length} results)
                    </h3>
                    <div className="flex items-center gap-4 text-white text-sm">
                      {searchStats && (
                        <>
                          <span>{searchStats.totalItems} items</span>
                          <span>•</span>
                          <span>{searchStats.uniqueLocations} locations</span>
                          <span>•</span>
                          <span>{searchStats.totalStories} stories</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {searchResults.length > 0 ? (
                  <div className="p-6">
                  {/* Search controls */}
                  <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary" className="text-sm">
                        Showing {Math.min(maxVisibleResults, searchResults.length)} of {searchResults.length} results
                      </Badge>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-3 py-1 border border-gray-200 rounded text-sm focus:border-emerald-500"
                      >
                        <option value="relevance">Sort: Relevance</option>
                        <option value="recent">Sort: Most Recent</option>
                        <option value="stories">Sort: Most Stories</option>
                        <option value="distance">Sort: Most Distance</option>
                      </select>
                    </div>
                    
                    <Button 
                      variant="outline" 
                      onClick={copySearchURL}
                      className="text-emerald-600 hover:text-emerald-700"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      Share Search
                    </Button>
                  </div>
                  
                  {/* Grid of search results */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {visibleSearchResults.map((item) => (
                      <Card 
                        key={item.uid} 
                        className="overflow-hidden shadow-md hover:shadow-lg transition-all cursor-pointer border border-gray-200 hover:border-emerald-300"
                        onClick={() => handleViewJourney(item)}
                      >
                        <CardContent className="p-4">
                          {/* Item thumbnail - Always Show Container */}
                          <div className="w-full h-32 bg-gray-200 rounded-lg overflow-hidden mb-3">
                            {(() => {
                              // Prefer the most-recent story image (dynamic
                              // per new upload). Fall back to initial /
                              // location photo only when no story has one.
                              let mostRecentStoryImage = null;
                              if (item.stories) {
                                for (let i = item.stories.length - 1; i >= 0; i--) {
                                  const s = item.stories[i];
                                  const img = s?.image || s?.imageUrl;
                                  if (img) { mostRecentStoryImage = img; break; }
                                }
                              }
                              const finalUrl = mostRecentStoryImage
                                || (item.itemType === 'stationary'
                                  ? (item.locationPhotoUrl || item.initialImageUrl)
                                  : (item.initialImageUrl || item.locationPhotoUrl));
                              
                              if (finalUrl) {
                                return (
                                  <img 
                                    src={finalUrl} 
                                    alt={`${item.name}`}
                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                    onError={(e) => {
                                      console.error(`Image load failed for ${item.name}: ${finalUrl}`);
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                );
                              } else {
                                return (
                                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-500">
                                    <Package className="w-8 h-8 mb-1" />
                                    <span className="text-xs font-medium">No Image</span>
                                  </div>
                                );
                              }
                            })()}
                          </div>
                          
                          {/* Item details */}
                          <div className="space-y-2">
                            <h4 className="font-bold text-sm text-gray-900 truncate">{item.name}</h4>
                            <p className="text-xs text-gray-600">
                              📍 {item.currentLocation}
                            </p>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>{item.distance}</span>
                              <span>{item.stories?.length || 0} stories</span>
                            </div>
                            
                            {/* Latest story snippet */}
                            {item.stories && item.stories.length > 0 && (
                              <p className="text-xs text-gray-600 italic line-clamp-2">
                                "{(item.stories[item.stories.length - 1].story || 'No story text').substring(0, 80)}..."
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  
                  {/* Show more button */}
                  {searchResults.length > maxVisibleResults && (
                    <div className="mt-6 text-center">
                      <Button
                        variant="outline"
                        onClick={() => setMaxVisibleResults(prev => prev + 12)}
                        className="px-6 py-2"
                      >
                        Show {Math.min(12, searchResults.length - maxVisibleResults)} More Results
                      </Button>
                    </div>
                  )}
                </div>
                ) : (
                  <div className="p-6 text-center text-gray-500">
                    <p>No results found for "{searchTerm}"</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};